/**
 * Modal for adding or editing a chat service connection (ChatGPT, Azure OpenAI, Gemini).
 * Includes validate-then-save flow and provider-specific fields.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  Box,
  CircularProgress,
  FormLabel,
  Autocomplete,
} from '@mui/material';
import { Visibility, VisibilityOff, Close as CloseIcon, CheckCircle } from '@mui/icons-material';
import {
  validateToken,
  saveTokenDetails,
  updateTokenDetails,
} from '../../services/chatServiceTokenService';
import type { SaveTokenDetailsPayload, UpdateTokenDetailsPayload } from '../../services/chatServiceTokenService';
import {
  getModalTitle,
  getApiKeyHelpUrl,
  getLlmTypeForValidation,
  getTokenTypeForApi,
  getModelOptions,
  EMBEDDING_MODEL_OPTIONS,
} from './constants';
import type { ModelOption } from './constants';
import type { ChatServiceProviderId } from './types';
import type { SavedTokenDisplay } from './types';

// --- Form state types per provider (inline for modal-only use) ---
interface ChatGPTForm {
  tokenName: string;
  apiKey: string;
  model: string;
  embeddingModel: string;
}
interface AzureForm {
  tokenName: string;
  endpoint: string;
  apiVersion: string;
  apiKey: string;
  deploymentName: string;
  embeddingDeploymentName: string;
  model: string;
}
interface GeminiForm {
  tokenName: string;
  apiKey: string;
  model: string;
  embeddingModel: string;
}

const defaultChatGPTForm: ChatGPTForm = {
  tokenName: '',
  apiKey: '',
  model: 'gpt-5.2',
  embeddingModel: 'text-embedding-ada-002',
};
const defaultAzureForm: AzureForm = {
  tokenName: '',
  endpoint: '',
  apiVersion: '',
  apiKey: '',
  deploymentName: '',
  embeddingDeploymentName: '',
  model: 'gpt-52-chat',
};
const defaultGeminiForm: GeminiForm = {
  tokenName: '',
  apiKey: '',
  model: 'gemini-3-pro-preview',
  embeddingModel: 'text-embedding-ada-002',
};

/** Red required asterisk for all modal labels */
const requiredLabelSx = {
  '& .MuiFormLabel-asterisk': { color: 'error.main' },
};

/** Base64-encode API key for backend (API keys are typically ASCII; btoa is safe) */
function encodeApiKeyBase64(apiKey: string): string {
  try {
    return btoa(apiKey.trim());
  } catch {
    return apiKey.trim();
  }
}

export interface ChatServiceModalProps {
  open: boolean;
  onClose: () => void;
  provider: ChatServiceProviderId;
  companyId: string;
  /** When set, modal is in edit mode and we call update API */
  editToken?: SavedTokenDisplay | null;
  onSaved: () => void;
}

const ChatServiceModal: React.FC<ChatServiceModalProps> = ({
  open,
  onClose,
  provider,
  companyId,
  editToken,
  onSaved,
}) => {
  const [chatGptForm, setChatGptForm] = useState<ChatGPTForm>(defaultChatGPTForm);
  const [azureForm, setAzureForm] = useState<AzureForm>(defaultAzureForm);
  const [geminiForm, setGeminiForm] = useState<GeminiForm>(defaultGeminiForm);
  const [showApiKey, setShowApiKey] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validateLoading, setValidateLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  /** Auto-hide success message after 4 seconds */
  useEffect(() => {
    if (!showSuccessAlert) return;
    const timer = window.setTimeout(() => setShowSuccessAlert(false), 4000);
    return () => window.clearTimeout(timer);
  }, [showSuccessAlert]);

  const isEdit = Boolean(editToken?.vault_unique_id || editToken?.giggso_vault_id);

  const resetState = () => {
    setChatGptForm(defaultChatGPTForm);
    setAzureForm(defaultAzureForm);
    setGeminiForm(defaultGeminiForm);
    setShowApiKey(false);
    setValidationSuccess(false);
    setShowSuccessAlert(false);
    setErrorMessage(null);
    setValidateLoading(false);
    setSaveLoading(false);
  };

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (editToken?.raw && typeof editToken.raw === 'object') {
      const r = editToken.raw as Record<string, unknown>;
      const savedModel = (r.gpt_model as string) ?? '';
      if (provider === 'ChatGPT') {
        const savedEmbedding = (r.embedding_model as string) ?? '';
        setChatGptForm({
          tokenName: (r.name as string) ?? editToken.displayLabel ?? '',
          apiKey: (r.api_key as string) ?? '',
          model: savedModel || defaultChatGPTForm.model,
          embeddingModel: savedEmbedding || defaultChatGPTForm.embeddingModel,
        });
      }
      if (provider === 'Gemini') {
        const savedEmbedding = (r.embedding_model as string) ?? '';
        setGeminiForm({
          tokenName: (r.name as string) ?? editToken.displayLabel ?? '',
          apiKey: (r.api_key as string) ?? '',
          model: savedModel || defaultGeminiForm.model,
          embeddingModel: savedEmbedding || defaultGeminiForm.embeddingModel,
        });
      }
      if (provider === 'AzureOpenAI') {
        setAzureForm({
          tokenName: (r.name as string) ?? editToken.displayLabel ?? '',
          endpoint: (r.endpoint as string) ?? '',
          apiVersion: (r.deploymentVersion as string) ?? '',
          apiKey: (r.api_key as string) ?? '',
          deploymentName: (r.deploymentName as string) ?? '',
          embeddingDeploymentName: (r.embedding_model as string) ?? '',
          model: savedModel || defaultAzureForm.model,
        });
      }
    } else {
      // Opening for "Add" (no editToken): reset to defaults so embedding model etc. don't persist
      resetState();
    }
  }, [open, provider, editToken]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleValidate = async () => {
    setErrorMessage(null);
    const llmType = getLlmTypeForValidation(provider);
    let payload: { llmType: string; llmApiKey: string; llmModelName?: string; llmApiVersion?: string; llmEndpoint?: string };

    if (provider === 'ChatGPT') {
      if (!chatGptForm.apiKey.trim()) {
        setErrorMessage('Please enter API key');
        return;
      }
      payload = { llmType, llmApiKey: encodeApiKeyBase64(chatGptForm.apiKey), llmModelName: chatGptForm.model || 'gpt-5.2' };
    } else if (provider === 'AzureOpenAI') {
      if (!azureForm.apiKey.trim() || !azureForm.endpoint.trim() || !azureForm.deploymentName.trim()) {
        setErrorMessage('Please fill required fields: Endpoint, API key, Deployment name');
        return;
      }
      payload = {
        llmType,
        llmApiKey: encodeApiKeyBase64(azureForm.apiKey),
        llmEndpoint: azureForm.endpoint.trim(),
        llmApiVersion: azureForm.apiVersion.trim() || undefined,
        llmModelName: azureForm.deploymentName.trim(),
      };
    } else {
      if (!geminiForm.apiKey.trim()) {
        setErrorMessage('Please enter API key');
        return;
      }
      payload = { llmType, llmApiKey: encodeApiKeyBase64(geminiForm.apiKey), llmModelName: geminiForm.model || 'gemini-3-pro-preview' };
    }

    setValidateLoading(true);
    try {
      await validateToken(payload);
      setValidationSuccess(true);
      setShowSuccessAlert(true);
      setErrorMessage(null);
    } catch (err) {
      setValidationSuccess(false);
      setErrorMessage(err instanceof Error ? err.message : 'Invalid credentials. Please check and try again.');
    } finally {
      setValidateLoading(false);
    }
  };

  const buildSavePayload = (): SaveTokenDetailsPayload => {
    const base: SaveTokenDetailsPayload = {
      token_type: getTokenTypeForApi(provider),
      companyId,
      api_key: '',
    };
    if (provider === 'ChatGPT') {
      base.api_key = encodeApiKeyBase64(chatGptForm.apiKey);
      base.gpt_model = chatGptForm.model || '';
      base.embedding_model = chatGptForm.embeddingModel || undefined;
      base.tokenName = chatGptForm.tokenName.trim() || undefined;
    } else if (provider === 'AzureOpenAI') {
      base.api_key = encodeApiKeyBase64(azureForm.apiKey);
      base.endpoint = azureForm.endpoint.trim();
      base.deploymentVersion = azureForm.apiVersion.trim();
      base.deploymentName = azureForm.deploymentName.trim();
      base.embedding_model = azureForm.embeddingDeploymentName.trim();
      base.gpt_model = azureForm.model || '';
      base.tokenName = azureForm.tokenName.trim() || undefined;
    } else {
      base.api_key = encodeApiKeyBase64(geminiForm.apiKey);
      base.gpt_model = geminiForm.model || '';
      base.embedding_model = geminiForm.embeddingModel || undefined;
      base.tokenName = geminiForm.tokenName.trim() || undefined;
    }
    return base;
  };

  const handleSave = async () => {
    if (!validationSuccess && !isEdit) return;
    setErrorMessage(null);
    setSaveLoading(true);
    try {
      const payload = buildSavePayload();
      if (isEdit && (editToken?.vault_unique_id || editToken?.giggso_vault_id)) {
        const updatePayload: UpdateTokenDetailsPayload = {
          ...payload,
          vault_unique_id: editToken.vault_unique_id ?? '',
          giggso_vault_id: editToken.giggso_vault_id ?? '',
        };
        await updateTokenDetails(updatePayload);
      } else {
        await saveTokenDetails(payload);
      }
      handleClose();
      onSaved();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaveLoading(false);
    }
  };

  const canSave = validationSuccess || isEdit;
  const title = getModalTitle(provider);
  const helpUrl = getApiKeyHelpUrl(provider);
  const modelOptions = getModelOptions(provider);

  return (
    <Dialog
        open={open}
        onClose={(_, reason) => {
          // Only close when user clicks X or Cancel; ignore Escape and backdrop click
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1, pb: 2 }}>
        <span>{title}</span>
        <IconButton aria-label="Close" onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}
        {showSuccessAlert && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setShowSuccessAlert(false)}>
            Your API key has been validated successfully.
          </Alert>
        )}

        {provider === 'ChatGPT' && (
          <Box>
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Name your API key
            </FormLabel>
            <TextField
              fullWidth
              placeholder="Name your API key"
              value={chatGptForm.tokenName}
              onChange={(e) => setChatGptForm((p) => ({ ...p, tokenName: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Model
            </FormLabel>
            <Autocomplete
              fullWidth
              options={modelOptions}
              value={modelOptions.find((o) => o.value === chatGptForm.model) ?? null}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              onChange={(_, newValue) =>
                setChatGptForm((p) => ({ ...p, model: (newValue as ModelOption | null)?.value ?? '' }))
              }
              renderInput={(params) => <TextField {...params} placeholder="Search or select model" />}
              sx={{ mb: 2 }}
            />
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Enter API key
            </FormLabel>
            <TextField
              fullWidth
              type={showApiKey ? 'text' : 'password'}
              placeholder="API key"
              value={chatGptForm.apiKey}
              onChange={(e) => setChatGptForm((p) => ({ ...p, apiKey: e.target.value }))}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowApiKey(!showApiKey)} aria-label="toggle visibility">
                      {showApiKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Embedding model
            </FormLabel>
            <Autocomplete
              fullWidth
              options={EMBEDDING_MODEL_OPTIONS}
              value={EMBEDDING_MODEL_OPTIONS.find((o) => o.value === chatGptForm.embeddingModel) ?? null}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              onChange={(_, newValue) =>
                setChatGptForm((p) => ({
                  ...p,
                  embeddingModel: (newValue as ModelOption | null)?.value ?? defaultChatGPTForm.embeddingModel,
                }))
              }
              renderInput={(params) => <TextField {...params} placeholder="Select embedding model" />}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ mb: 2 }}>
              <Link href={helpUrl} target="_blank" rel="noopener noreferrer">
                Need help generating API key?
              </Link>
            </Typography>
            <Box sx={{ mt: 2 }}>
              {validationSuccess ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                  <CheckCircle fontSize="small" />
                  <Typography variant="body2" component="span">
                    Validated
                  </Typography>
                </Box>
              ) : (
                <Button variant="outlined" onClick={handleValidate} disabled={validateLoading}>
                  {validateLoading ? <CircularProgress size={24} /> : 'Validate key'}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {provider === 'AzureOpenAI' && (
          <Box>
            {/* Row 1: Name your API key */}
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Name your API key
            </FormLabel>
            <TextField
              fullWidth
              placeholder="Name your API key"
              value={azureForm.tokenName}
              onChange={(e) => setAzureForm((p) => ({ ...p, tokenName: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Model
            </FormLabel>
            <Autocomplete
              fullWidth
              options={modelOptions}
              value={modelOptions.find((o) => o.value === azureForm.model) ?? null}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              onChange={(_, newValue) =>
                setAzureForm((p) => ({ ...p, model: (newValue as ModelOption | null)?.value ?? '' }))
              }
              renderInput={(params) => <TextField {...params} placeholder="Search or select model" />}
              sx={{ mb: 2 }}
            />
            {/* Row 2: Endpoint & API version (50% / 50%) */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
                  Endpoint
                </FormLabel>
                <TextField
                  fullWidth
                  placeholder="Enter endpoint"
                  value={azureForm.endpoint}
                  onChange={(e) => setAzureForm((p) => ({ ...p, endpoint: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
                  API version
                </FormLabel>
                <TextField
                  fullWidth
                  placeholder="Enter API version"
                  value={azureForm.apiVersion}
                  onChange={(e) => setAzureForm((p) => ({ ...p, apiVersion: e.target.value }))}
                />
              </Box>
            </Box>
            {/* Row 3: API key */}
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              API key
            </FormLabel>
            <TextField
              fullWidth
              type={showApiKey ? 'text' : 'password'}
              placeholder="Enter API key"
              value={azureForm.apiKey}
              onChange={(e) => setAzureForm((p) => ({ ...p, apiKey: e.target.value }))}
              error={Boolean(errorMessage)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowApiKey(!showApiKey)} aria-label="toggle visibility">
                      {showApiKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            {/* Row 4: Deployment name & Embeddings deployment name (50% / 50%) */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
                  Deployment name
                </FormLabel>
                <TextField
                  fullWidth
                  placeholder="Enter deployment name"
                  value={azureForm.deploymentName}
                  onChange={(e) => setAzureForm((p) => ({ ...p, deploymentName: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
                  Embeddings deployment name
                </FormLabel>
                <TextField
                  fullWidth
                  placeholder="Enter embeddings deployment name"
                  value={azureForm.embeddingDeploymentName}
                  onChange={(e) => setAzureForm((p) => ({ ...p, embeddingDeploymentName: e.target.value }))}
                />
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              {validationSuccess ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                  <CheckCircle fontSize="small" />
                  <Typography variant="body2" component="span">
                    Validated
                  </Typography>
                </Box>
              ) : (
                <Button variant="outlined" onClick={handleValidate} disabled={validateLoading}>
                  {validateLoading ? <CircularProgress size={24} /> : 'Validate key'}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {provider === 'Gemini' && (
          <Box>
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Name your API key
            </FormLabel>
            <TextField
              fullWidth
              placeholder="Name your API key"
              value={geminiForm.tokenName}
              onChange={(e) => setGeminiForm((p) => ({ ...p, tokenName: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Model
            </FormLabel>
            <Autocomplete
              fullWidth
              options={modelOptions}
              value={modelOptions.find((o) => o.value === geminiForm.model) ?? null}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              onChange={(_, newValue) =>
                setGeminiForm((p) => ({ ...p, model: (newValue as ModelOption | null)?.value ?? '' }))
              }
              renderInput={(params) => <TextField {...params} placeholder="Search or select model" />}
              sx={{ mb: 2 }}
            />
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Enter API key
            </FormLabel>
            <TextField
              fullWidth
              type={showApiKey ? 'text' : 'password'}
              placeholder="API key"
              value={geminiForm.apiKey}
              onChange={(e) => setGeminiForm((p) => ({ ...p, apiKey: e.target.value }))}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowApiKey(!showApiKey)} aria-label="toggle visibility">
                      {showApiKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <FormLabel required sx={{ display: 'block', mb: 1, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
              Embedding model
            </FormLabel>
            <Autocomplete
              fullWidth
              options={EMBEDDING_MODEL_OPTIONS}
              value={EMBEDDING_MODEL_OPTIONS.find((o) => o.value === geminiForm.embeddingModel) ?? null}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              onChange={(_, newValue) =>
                setGeminiForm((p) => ({
                  ...p,
                  embeddingModel: (newValue as ModelOption | null)?.value ?? defaultGeminiForm.embeddingModel,
                }))
              }
              renderInput={(params) => <TextField {...params} placeholder="Select embedding model" />}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ mb: 2 }}>
              <Link href={helpUrl} target="_blank" rel="noopener noreferrer">
                Need help generating API key?
              </Link>
            </Typography>
            <Box sx={{ mt: 2 }}>
              {validationSuccess ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                  <CheckCircle fontSize="small" />
                  <Typography variant="body2" component="span">
                    Validated
                  </Typography>
                </Box>
              ) : (
                <Button variant="outlined" onClick={handleValidate} disabled={validateLoading}>
                  {validateLoading ? <CircularProgress size={24} /> : 'Validate key'}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saveLoading}>
          {saveLoading ? <CircularProgress size={24} /> : isEdit ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatServiceModal;
